"use client";

import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { RADIUS } from "src/themes/tokens";

/**
 * The compact vocabulary for a phone's options sheet, where the live preview
 * sits above and the choices have to fit under it: a small bold label per
 * question and a row of chips per answer, in place of StepHeading and
 * OptionCard. Desktop keeps the large numbered cards.
 */
export function CompactHeading({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <Typography
      id={id}
      component="h3"
      sx={(theme) => ({ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: theme.tokens.ink, mb: 1 })}
    >
      {children}
    </Typography>
  );
}

/** A wrapping row of chips. */
export const chipRowSx = { display: "flex", flexWrap: "wrap", gap: 1 } as const;

export interface ChoiceChipProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** "radio" inside a radiogroup (the default); "switch" for an on/off chip. */
  role?: "radio" | "switch";
  /** A second, quieter line, e.g. the price. */
  detail?: React.ReactNode;
  disabled?: boolean;
}

/** One answer as a pill: dark when chosen, outlined when not. */
export function ChoiceChip({ selected, onClick, children, role = "radio", detail, disabled }: ChoiceChipProps) {
  return (
    <ButtonBase
      data-choice-chip=""
      role={role}
      aria-checked={selected}
      onClick={onClick}
      disabled={disabled}
      focusRipple
      sx={(theme) => ({
        minHeight: 40,
        px: 1.75,
        py: detail ? 0.5 : 0,
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: `${RADIUS.pill}px`,
        border: "1px solid",
        borderColor: selected ? theme.tokens.ink : theme.palette.grey[300],
        bgcolor: selected ? theme.tokens.ink : "transparent",
        color: selected ? theme.palette.background.paper : theme.tokens.ink,
        fontFamily: theme.typography.fontFamily,
        fontSize: 14,
        fontWeight: selected ? 700 : 500,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        transition: "background-color 120ms, border-color 120ms",
        "&.Mui-focusVisible": { outline: `3px solid ${theme.tokens.brand}`, outlineOffset: 2 },
        "&.Mui-disabled": { opacity: 0.45 },
      })}
    >
      <Box component="span">{children}</Box>
      {detail && (
        <Box component="span" sx={{ fontSize: 12, fontWeight: 600, opacity: selected ? 0.85 : 0.75 }}>
          {detail}
        </Box>
      )}
    </ButtonBase>
  );
}
